import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse } from './baseApi';

export type NoteType = 'CREDIT_NOTE' | 'DEBIT_NOTE';

export type NoteStatus = string; // e.g. DRAFT, SUBMITTED - align with backend NoteStatus enum

/** Header DTO aligned with backend CreditDebitNoteHeaderDto */
export interface CreditDebitNoteHeaderDto {
  id?: number;
  noteType?: NoteType;
  noteNumber?: string;
  noteDate?: string;
  referenceInvoiceId?: number;
  branchId?: number;
  shipTo?: number;
  billTo?: number;
  reason?: string;
  grandTotal?: number;
  grandTotalTax?: number;
  totalAmount?: number;
  status?: NoteStatus;
  irn?: string;
  ackNo?: string;
  ackDate?: string;
  /** E-invoice QR payload (API may use alternate spellings; view layer checks those too) */
  signedQrcode?: string;
  signedBy?: number;
  signedByName?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number;
  updatedBy?: number;
  branchName?: string;
  shipToName?: string;
  billToName?: string;
  referenceInvoiceNo?: string;
  details?: CreditDebitNoteDetailDto[];
  [key: string]: unknown;
}

/** Detail DTO aligned with backend CreditDebitNoteDetailDto (get-by-id response includes endgoodName, lineTotal, etc.) */
export interface CreditDebitNoteDetailDto {
  id?: number;
  creditDebitNoteHeaderId?: number;
  referenceInvoiceDetailId?: number | null;
  endgoodId: number;
  goodName?: string;
  endgoodName?: string;
  endgoodCode?: string;
  uom?: string;
  qty: number;
  rate: number;
  hsn?: string;
  cgst?: number;
  sgst?: number;
  igst?: number;
  amount?: number;
  lineTotal?: number;
  totalTax?: number;
  typeOfGood?: string;
  [key: string]: unknown;
}

/** Complete create request (header + details) - same pattern as invoice POST */
export interface CompleteCreditDebitNoteCreateRequest extends Omit<CreditDebitNoteHeaderDto, 'id' | 'details' | 'branchName' | 'shipToName' | 'billToName' | 'referenceInvoiceNo'> {
  noteType: NoteType;
  noteDate: string;
  referenceInvoiceId: number;
  branchId: number;
  shipTo: number;
  billTo: number;
  totalAmount: number;
  grandTotalTax: number;
  grandTotal: number;
  details: Omit<CreditDebitNoteDetailDto, 'id'>[];
}

/** Complete update request (header + details) */
export interface CompleteCreditDebitNoteUpdateRequest extends Partial<CompleteCreditDebitNoteCreateRequest> {
  id: number;
  details?: (CreditDebitNoteDetailDto & { id?: number })[];
}

/** List row status — align with backend (e.g. DRAFT, E_INVOICED). */
export const CreditDebitNoteListStatus = {
  DRAFT: 'DRAFT',
  E_INVOICED: 'E_INVOICED',
} as const;

export interface CreditDebitNoteHeader {
  id: number;
  creditDebitNoteNo?: string;
  creditDate?: string;
  invoiceHeaderId?: number;
  invoiceNo?: string;
  invoiceDate?: string;
  /** Present on list/detail when backend sends it */
  status?: string;
  noteType?: NoteType;
  billTo?: number;
  shipTo?: number;
  sellerId?: number;
  branchId?: number;
  fromSalesOrder?: boolean;
  referenceNoAndDate?: string;
  otherReferences?: string;
  buyerOrderNo?: string;
  buyerOrderDate?: string;
  dispatchDocNo?: string;
  dispatchedThrough?: string;
  destination?: string;
  termsOfDelivery?: string;
  totalAmount?: number;
  grandTotalTax?: number;
  grandTotal?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: number;
  updatedBy?: number;
  [key: string]: unknown;
}

export interface CreditDebitNoteItem {
  id?: number;
  creditDebitHeaderId: number;
  endgoodId: number;
  goodName?: string;
  uom?: string;
  qty: number;
  rate: number;
  hsn?: string;
  cgst?: number;
  sgst?: number;
  igst?: number;
  amount?: number;
  boxes?: string;
  heatNo?: string;
  userProvidedHeatNo?: string;
  typeOfGood?: string;
  selectedItem?: 'Y' | 'N';
  [key: string]: unknown;
}

/** @deprecated Use CompleteCreditDebitNoteCreateRequest for create */
export interface CreditDebitNoteCreateRequest {
  branchId: number;
  invoiceHeaderId: number;
  invoiceNo: string;
  invoiceDate?: string;
  noteType: NoteType;
  creditDate: string;
  billTo: number;
  shipTo: number;
  sellerId: number;
  fromSalesOrder: boolean;
  referenceNoAndDate?: string;
  otherReferences?: string;
  buyerOrderNo?: string;
  buyerOrderDate?: string;
  dispatchDocNo?: string;
  dispatchedThrough?: string;
  destination?: string;
  termsOfDelivery?: string;
  totalAmount?: number;
  grandTotalTax?: number;
  grandTotal?: number;
  [key: string]: unknown;
}

class CreditDebitNoteApiService extends BaseApiService {
  async getList(branchId?: number): Promise<CreditDebitNoteHeader[]> {
    try {
      const url = API_ENDPOINTS.CREDIT_DEBIT_NOTES.LIST(branchId);
      const response = await this.makeRequest<ApiResponse<CreditDebitNoteHeader[]>>(url, { method: HTTP_METHODS.GET });
      if (response.success && response.data) return Array.isArray(response.data) ? response.data : [];
      return [];
    } catch (e) {
      toast.error('Failed to load credit/debit notes');
      throw e;
    }
  }

  /** Get by id returns full DTO including details (no separate items call needed). */
  async getById(id: string): Promise<CreditDebitNoteHeaderDto | null> {
    try {
      const response = await this.makeRequest<ApiResponse<CreditDebitNoteHeaderDto>>(
        API_ENDPOINTS.CREDIT_DEBIT_NOTES.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      if (response.success && response.data) return response.data;
      return null;
    } catch (e) {
      toast.error('Failed to load credit/debit note');
      throw e;
    }
  }

  /** Create complete credit/debit note (header + details) - same pattern as invoice POST */
  async createCompleteCreditDebitNote(data: CompleteCreditDebitNoteCreateRequest): Promise<ApiResponse<CreditDebitNoteHeaderDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<CreditDebitNoteHeaderDto>>(
        API_ENDPOINTS.CREDIT_DEBIT_NOTES.CREATE,
        { method: HTTP_METHODS.POST, body: JSON.stringify(data) }
      );
      if (response.success) toast.success('Credit/Debit note created');
      return response;
    } catch (e) {
      toast.error('Failed to create credit/debit note');
      throw e;
    }
  }

  /** Update complete credit/debit note (header + details) - same pattern as invoice PUT */
  async updateCompleteCreditDebitNote(id: string, data: CompleteCreditDebitNoteUpdateRequest): Promise<ApiResponse<CreditDebitNoteHeaderDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<CreditDebitNoteHeaderDto>>(
        API_ENDPOINTS.CREDIT_DEBIT_NOTES.UPDATE(id),
        { method: HTTP_METHODS.PUT, body: JSON.stringify(data) }
      );
      if (response.success) toast.success('Credit/Debit note updated');
      return response;
    } catch (e) {
      toast.error('Failed to update credit/debit note');
      throw e;
    }
  }

  async generateEInvoice(id: string): Promise<ApiResponse<CreditDebitNoteHeaderDto>> {
    try {
      return await this.makeRequest<ApiResponse<CreditDebitNoteHeaderDto>>(
        API_ENDPOINTS.CREDIT_DEBIT_NOTES.GENERATE_EINVOICE(id),
        { method: HTTP_METHODS.POST }
      );
    } catch (e) {
      toast.error('Failed to generate e-invoice');
      throw e;
    }
  }

  async create(data: CreditDebitNoteCreateRequest): Promise<CreditDebitNoteHeader> {
    const response = await this.makeRequest<ApiResponse<CreditDebitNoteHeader>>(
      API_ENDPOINTS.CREDIT_DEBIT_NOTES.CREATE,
      { method: HTTP_METHODS.POST, body: JSON.stringify(data) }
    );
    if (!response.success || !response.data) throw new Error(response.message || 'Failed to create note');
    toast.success('Credit/Debit note created');
    return response.data;
  }

  async update(id: string, data: Partial<CreditDebitNoteHeader> & { id: number }): Promise<CreditDebitNoteHeader> {
    const response = await this.makeRequest<ApiResponse<CreditDebitNoteHeader>>(
      API_ENDPOINTS.CREDIT_DEBIT_NOTES.UPDATE(id),
      { method: HTTP_METHODS.PUT, body: JSON.stringify(data) }
    );
    if (!response.success || !response.data) throw new Error(response.message || 'Failed to update note');
    toast.success('Credit/Debit note updated');
    return response.data;
  }

  async getItemsByHeaderId(headerId: string): Promise<CreditDebitNoteItem[]> {
    try {
      const response = await this.makeRequest<ApiResponse<CreditDebitNoteItem[]>>(
        API_ENDPOINTS.CREDIT_DEBIT_NOTES.ITEMS_BY_HEADER(headerId),
        { method: HTTP_METHODS.GET }
      );
      if (response.success && response.data) return Array.isArray(response.data) ? response.data : [];
      return [];
    } catch (e) {
      toast.error('Failed to load note items');
      throw e;
    }
  }

  async saveItems(headerId: string, items: Omit<CreditDebitNoteItem, 'id'>[]): Promise<void> {
    const response = await this.makeRequest<ApiResponse<unknown>>(
      API_ENDPOINTS.CREDIT_DEBIT_NOTES.SAVE_ITEMS(headerId),
      { method: HTTP_METHODS.POST, body: JSON.stringify(items) }
    );
    if (!response.success) throw new Error(response.message || 'Failed to save items');
  }
}

export const creditDebitNoteApiService = new CreditDebitNoteApiService();
